import AccountBalanceIcon from '@material-ui/icons/AccountBalance'
import BuildIcon from '@material-ui/icons/Build'
import BusinessIcon from '@material-ui/icons/Business'
import CheckCircleOutline from '@material-ui/icons/CheckCircleOutline'
import CloudDoneIcon from '@material-ui/icons/CloudDone'
import ContactPhoneIcon from '@material-ui/icons/ContactPhone'
import DirectionsBoatIcon from '@material-ui/icons/DirectionsBoat'
import GroupIcon from '@material-ui/icons/Group'
import LocalGasStation from '@material-ui/icons/LocalGasStation'
import LocalShippingIcon from '@material-ui/icons/LocalShipping'
import LockIcon from '@material-ui/icons/Lock'
import MapIcon from '@material-ui/icons/Map'
import PersonIcon from '@material-ui/icons/Person'
import PolicyIcon from '@material-ui/icons/Policy'
import PriorityHighIcon from '@material-ui/icons/PriorityHigh'
import ReceiptIcon from '@material-ui/icons/Receipt'
import ReportProblemIcon from '@material-ui/icons/ReportProblem'
import ScheduleIcon from '@material-ui/icons/Schedule'
import SearchIcon from '@material-ui/icons/Search'
import ThumbUpIcon from '@material-ui/icons/ThumbUp'
import WorkIcon from '@material-ui/icons/Work'
import { MultipleStop } from '@mui/icons-material'
import { googleApiReducer, snackbarsReducer } from 'customReducers'
import { createBrowserHistory as createHistory } from 'history'
import polyglotI18nProvider from 'ra-i18n-polyglot'
import englishMessages from 'ra-language-english'
import { ComponentType } from 'react'
import { Admin, Resource, ResourceComponentPropsWithId } from 'react-admin'

import { CTXLoadList } from 'components/CTXLoads/CTXLoadList'
import { CTXLoadShow } from 'components/CTXLoads/CTXLoadShow'
import { FeasibilityStatList } from 'components/FeasibilityStats/FeasibilityStatList'
import IssueShow from 'components/Issues/IssueShow'
import JobTrackingDashboard from 'components/Jobs/JobTrackingDashboard'
import { EDILoadShow } from 'components/Loads/EDILoadShow'
import { SafetyToolList } from 'components/SafetyTool/SafetyToolList'
import { SFMSHomepageShow } from 'components/SFMS/SFMSHomepageShow'
import { SFMSInvoicesList } from 'components/SFMS/SFMSInvoicesList'
import { SFMSPickupAtRiskList } from 'components/SFMS/SFMSPickupAtRiskList'
import { SFMSSchedulingList } from 'components/SFMS/SFMSSchedulingList'
import { SFMSWaitingDriverAssignmentList } from 'components/SFMS/SFMSWaitingDriverAssignmentList'
import { SFMSWaitingTenderDecisionList } from 'components/SFMS/SFMSWaitingTenderDecisionList'
import BrokerEdit from './components/Brokers/BrokerEdit'
import BrokerList from './components/Brokers/BrokerList'
import { BrokerShow } from './components/Brokers/BrokerShow'
import ContactedLoadShow from './components/ContactedLoads/ContactedLoadShow'
import ContactedLoadsList from './components/ContactedLoads/ContactedLoadsList'
import CustomLoadCreate from './components/CustomLoads/CustomLoadCreate'
import CustomLoadEdit from './components/CustomLoads/CustomLoadEdit'
import CustomLoadList from './components/CustomLoads/CustomLoadList'
import CustomLoadShow from './components/CustomLoads/CustomLoadShow'
import { TrailerIcon } from './components/EquipmentManagement/TrailerIcon'
import { TrailerShow } from './components/EquipmentManagement/TrailerShow'
import { TrailersList } from './components/EquipmentManagement/TrailersList'
import FuelCreate from './components/FuelTax/FuelCreate'
import FuelEdit from './components/FuelTax/FuelEdit'
import FuelList from './components/FuelTax/FuelList'
import InvoiceEdit from './components/Invoices/InvoiceEdit'
import InvoiceList from './components/Invoices/InvoiceList'
import GenericIssueList from './components/Issues/GenericIssueList'
import JobIssueList from './components/Issues/JobIssueList'
import LoadIssueList from './components/Issues/LoadIssueList'
import JobEdit from './components/Jobs/JobEdit'
import JobList from './components/Jobs/JobList'
import JobShow from './components/Jobs/JobShow'
import LoadCreate from './components/Loads/LoadCreate'
import LoadList from './components/Loads/LoadList'
import LoadShow from './components/Loads/LoadShow'
import PermissionGroupList from './components/PermissionGroups/PermissionGroupList'
import PermissionGroupShow from './components/PermissionGroups/PermissionGroupShow'
import QAEdit from './components/QA/QAEdit'
import QAList from './components/QA/QAList'
import ReferralCreate from './components/Referrals/ReferralCreate'
import ReferralList from './components/Referrals/ReferralList'
import ScheduleList from './components/Schedules/ScheduleList'
import ScheduleShow from './components/Schedules/ScheduleShow'
import SearchCreate from './components/Searches/SearchCreate'
import SearchList from './components/Searches/SearchList'
import SearchShow from './components/Searches/SearchShow'
import StatementEdit from './components/Statements/StatementEdit'
import StatementList from './components/Statements/StatementList'
import StatementShow from './components/Statements/StatementShow'
import TaskList from './components/Tasks/TaskList'
import TaskShow from './components/Tasks/TaskShow'
import TeamList from './components/Teams/TeamList'
import TeamShow from './components/Teams/TeamShow'
import UserList from './components/Users/UserList'
import UserShow from './components/Users/UserShow'
import CustomLayout from './customLayout'
import customRoutes from './customRoutes'
import { apiUrl, fusionApiUrl } from './helpers/constants'
import { hasPermission, Permissions } from './Permissions'
import authProvider from './ra-providers/authProvider'
import drfProvider from './ra-providers/dataProvider'

const history = createHistory()

const i18nprovider = polyglotI18nProvider(() => englishMessages, 'en', {
  allowMissing: true,
  onMissingKey: (key: string) => key,
})

const dataProvider = (type: string, resource: string, params: any) => {
  if (resource === 'fuel-receipts') {
    return drfProvider(fusionApiUrl)(type, resource, params)
  }
  return drfProvider(apiUrl)(type, resource, params)
}

const App = () => (
  <Admin
    dataProvider={dataProvider}
    authProvider={authProvider}
    i18nProvider={i18nprovider}
    customRoutes={customRoutes}
    customReducers={{
      googleApi: googleApiReducer,
      snackbars: snackbarsReducer,
    }}
    history={history}
    disableTelemetry
    layout={CustomLayout}
  >
    {(permissions) => [
      hasPermission(permissions, Permissions.ViewCustomUser) ? (
        <Resource
          name="users"
          list={UserList}
          show={UserShow}
          options={{ label: 'Users' }}
          icon={PersonIcon}
        />
      ) : null,
      hasPermission(permissions, Permissions.ViewTeam) ? (
        <Resource
          name="teams"
          list={TeamList}
          show={TeamShow}
          options={{ label: 'Teams' }}
          icon={GroupIcon}
        />
      ) : null,
      hasPermission(permissions, Permissions.ViewJob) ? (
        <Resource
          name="jobs"
          list={JobList}
          show={JobShow as ComponentType<ResourceComponentPropsWithId<{}>>}
          edit={hasPermission(permissions, Permissions.ChangeJob) ? JobEdit : undefined}
          options={{ label: 'Jobs' }}
          icon={WorkIcon}
        />
      ) : null,
      hasPermission(permissions, Permissions.ViewStatement) ? (
        <Resource
          name="statements"
          list={StatementList}
          show={StatementShow as ComponentType<ResourceComponentPropsWithId<{}>>}
          edit={hasPermission(permissions, Permissions.ChangeStatement) ? StatementEdit : undefined}
          options={{ label: 'Statements' }}
          icon={AccountBalanceIcon}
        />
      ) : null,
      hasPermission(permissions, Permissions.ViewInvoice) ? (
        <Resource
          name="invoices"
          options={{ label: 'Invoices' }}
          edit={hasPermission(permissions, Permissions.ChangeInvoice) ? InvoiceEdit : undefined}
          list={InvoiceList}
          icon={ReceiptIcon}
        />
      ) : null,
      hasPermission(permissions, Permissions.ViewIssue) ? (
        <Resource
          name="issues"
          list={JobIssueList}
          show={IssueShow}
          icon={ReportProblemIcon}
          options={{ label: 'Job Issues' }}
        />
      ) : null,
      hasPermission(permissions, Permissions.ViewIssue) ? (
        <Resource
          name="load-issues"
          list={LoadIssueList}
          show={IssueShow}
          icon={ReportProblemIcon}
          options={{ label: 'Load Issues' }}
        />
      ) : null,
      hasPermission(permissions, Permissions.ViewIssue) ? (
        <Resource
          name="generic-issues"
          list={GenericIssueList}
          show={IssueShow}
          icon={ReportProblemIcon}
          options={{ label: 'Generic Issues' }}
        />
      ) : null,
      hasPermission(permissions, Permissions.ViewLoad) ? (
        <Resource
          name="loads"
          list={LoadList}
          show={LoadShow}
          create={hasPermission(permissions, Permissions.AddLoad) ? LoadCreate : undefined}
          options={{ label: 'Loads' }}
          icon={LocalShippingIcon}
        />
      ) : null,
      hasPermission(permissions, Permissions.ViewContactedLoad) ? (
        <Resource
          name="contacted-loads"
          list={ContactedLoadsList}
          show={ContactedLoadShow}
          icon={ContactPhoneIcon}
          options={{ label: 'Contacted Loads' }}
        />
      ) : null,
      hasPermission(permissions, Permissions.ViewCustomLoad) ? (
        <Resource
          name="custom-loads"
          list={CustomLoadList}
          show={CustomLoadShow}
          edit={hasPermission(permissions, Permissions.ChangeCustomLoad) ? CustomLoadEdit : undefined}
          create={hasPermission(permissions, Permissions.AddCustomLoad) ? CustomLoadCreate : undefined}
          icon={BuildIcon}
          options={{ label: 'Custom Loads' }}
        />
      ) : null,
      hasPermission(permissions, Permissions.ViewSFMS) ? (
        <Resource
          name="sfms/homepage/summary/show"
          show={SFMSHomepageShow}
          icon={DirectionsBoatIcon}
          options={{ label: 'Shipper Loads' }}
        />
      ) : null,
      hasPermission(permissions, Permissions.ViewCTXLoad) ? (
        <Resource
          name="ctx-loads"
          list={CTXLoadList}
          show={CTXLoadShow}
          icon={MultipleStop}
          options={{ label: 'CT Exchange Loads' }}
        />
      ) : null,
      hasPermission(permissions, Permissions.ViewSchedule) ? (
        <Resource
          name="schedules"
          list={ScheduleList}
          show={ScheduleShow}
          options={{ label: 'Schedules' }}
          icon={ScheduleIcon}
        />
      ) : null,
      hasPermission(permissions, Permissions.ViewLoad) ? (
        <Resource
          name="searches"
          list={SearchList}
          show={SearchShow}
          create={hasPermission(permissions, Permissions.AddLoad) ? SearchCreate : undefined}
          options={{ label: 'Searches' }}
          icon={SearchIcon}
        />
      ) : null,
      hasPermission(permissions, Permissions.ViewBroker) ? (
        <Resource
          name="brokers"
          list={BrokerList}
          show={BrokerShow}
          edit={hasPermission(permissions, Permissions.ChangeBroker) ? BrokerEdit : undefined}
          options={{ label: 'Brokers' }}
          icon={BusinessIcon}
        />
      ) : null,
      hasPermission(permissions, Permissions.ChangeJob) ? (
        <Resource
          name="tasks"
          list={TaskList}
          show={TaskShow}
          options={{ label: 'Tasks' }}
          icon={PriorityHighIcon}
        />
      ) : null,
      hasPermission(permissions, Permissions.ViewReferral) ? (
        <Resource
          name="referrals"
          list={ReferralList}
          create={ReferralCreate}
          options={{ label: 'Referrals' }}
          icon={ThumbUpIcon}
        />
      ) : null,
      // This makes RA aware of referral-codes as a resource, without creating a page for it, which is sufficent for now
      // May want to expand it to a proper page to manage referral campaigns in the future
      <Resource name="referral-codes" options={{ hide: true }} />,
      hasPermission(permissions, Permissions.ViewCredential) ? (
        <Resource name="connected-integrations" options={{ hide: true }} />
      ) : null,
      hasPermission(permissions, Permissions.ViewQA) && window.location.href.match(/(localhost|staging)/g) ? (
        <Resource
          name="qa"
          list={QAList}
          edit={QAEdit}
          options={{ label: 'QA Scenarios' }}
          icon={CloudDoneIcon}
        />
      ) : null,
      hasPermission(permissions, Permissions.ViewFuelReceipt) ? (
        <Resource
          name="fuel-receipts"
          list={FuelList}
          edit={hasPermission(permissions, Permissions.ChangeFuelReceipt) ? FuelEdit : undefined}
          create={hasPermission(permissions, Permissions.AddFuelReceipt) ? FuelCreate : undefined}
          options={{ label: 'Fuel Receipts' }}
          icon={LocalGasStation}
        />
      ) : null,
      hasPermission(permissions, Permissions.ViewFuelReceipt) ? (
        <Resource name="trucks" options={{ hide: true }} />
      ) : null,
      hasPermission(permissions, Permissions.ViewEquipment, 'public-equipment-management') ? (
        <Resource
          name="trailers"
          list={TrailersList}
          show={TrailerShow}
          options={{ label: 'Trailers' }}
          icon={TrailerIcon}
        />
      ) : null,
      hasPermission(permissions, Permissions.ViewJob) ? (
        <Resource
          name="job-tracking"
          list={JobTrackingDashboard}
          options={{ label: 'Job Tracking' }}
          icon={MapIcon}
        />
      ) : null,
      hasPermission(permissions, Permissions.ViewPermissionGroup) ? (
        <Resource
          name="permission-groups"
          list={PermissionGroupList}
          edit={
            hasPermission(permissions, Permissions.ChangePermissionGroup) ? PermissionGroupShow : undefined
          }
          options={{ label: 'Permission Groups' }}
          icon={LockIcon}
        />
      ) : null,
      hasPermission(permissions, Permissions.ViewSafetyTool) ? (
        <Resource
          name="compliance-safety-tool"
          list={SafetyToolList}
          options={{ label: 'Safety & Performance' }}
          icon={PolicyIcon}
        />
      ) : null,
      <Resource name="flags" options={{ hide: true }} />,
      <Resource name="carriers" options={{ hide: true }} />,
      <Resource name="search-loads" options={{ hide: true }} />,
      <Resource name="equipment-inspection-report" options={{ hide: true }} />,
      <Resource name="edi-loads" options={{ hide: true }} show={EDILoadShow} />,

      hasPermission(permissions, Permissions.ViewSFMS) ? (
        <Resource
          name="sfms/waiting-tender-decision"
          list={SFMSWaitingTenderDecisionList}
          options={{ hide: true }}
        />
      ) : null,
      hasPermission(permissions, Permissions.ViewSFMS) ? (
        <Resource name="sfms/scheduling" list={SFMSSchedulingList} options={{ hide: true }} />
      ) : null,
      hasPermission(permissions, Permissions.ViewSFMS) ? (
        <Resource
          name="sfms/driver-assignment/"
          list={SFMSWaitingDriverAssignmentList}
          options={{ hide: true }}
        />
      ) : null,
      hasPermission(permissions, Permissions.ViewSFMS) ? (
        <Resource name="sfms/pickup-at-risk" list={SFMSPickupAtRiskList} options={{ hide: true }} />
      ) : null,
      hasPermission(permissions, Permissions.ViewSFMS) ? (
        <Resource name="sfms/invoices" list={SFMSInvoicesList} options={{ hide: true }} />
      ) : null,
      hasPermission(permissions, Permissions.ViewFeasibilityStat) ? (
        <Resource
          name="feasibility-stat"
          list={FeasibilityStatList}
          show={TeamShow}
          options={{ label: 'Driver Feasibility' }}
          icon={CheckCircleOutline}
        />
      ) : null,
    ]}
  </Admin>
)

export default App
